import { useCallback, useRef } from "react";
import { Howl } from "howler";
import { kdsVoiceApi } from "../api/client";

/**
 * Audio hook per il KDS web — Feature 022.
 *
 * Espone:
 * - play(soundName): beep stock via Howler (file MP3 in public/sounds/).
 *   Usato per il "ding-ding" prima dell'annuncio vocale (FR-013).
 * - announce(eventType, payload): orchestratore voce KDS.
 *     1. Chiama backend POST /api/kitchen/tts/announce.
 *     2. Se silent → noop.
 *     3. Se audioUrl → enqueue play via new Audio(url).
 *     4. Se fallbackText → enqueue speak via window.speechSynthesis.
 *     5. Tutto serializzato (Promise queue, FR-014: no overlap).
 *
 * Browser autoplay policy: il primo announce/speak fallisce se non c'e' stata
 * gesture utente. AudioUnlockOverlay gestisce il primo tap.
 */

const SOUNDS = {
  newOrder: "/sounds/new-order.mp3",
  itemReady: "/sounds/item-ready.mp3",
  alert: "/sounds/alert.mp3",
} as const;

type SoundName = keyof typeof SOUNDS;

// Shared queue across hook instances: gli annunci vengono serializzati
// anche se hook chiamato da componenti diversi.
let activeQueue: Promise<void> = Promise.resolve();

function enqueue(task: () => Promise<void>): Promise<void> {
  activeQueue = activeQueue.then(task).catch(() => {});
  return activeQueue;
}

// Dedup degli annunci: il backend WS broadcast emette doppio (send locale +
// Redis sub re-emit). Skippiamo annunci identici entro DEDUP_WINDOW_MS dall'ultimo.
const DEDUP_WINDOW_MS = 2000;
const recentAnnouncements = new Map<string, number>();

function shouldDedup(key: string): boolean {
  const now = Date.now();
  const last = recentAnnouncements.get(key);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
    return true;
  }
  recentAnnouncements.set(key, now);
  // Cleanup keys piu' vecchie di 30s per evitare memory leak
  if (recentAnnouncements.size > 50) {
    for (const [k, t] of recentAnnouncements) {
      if (now - t > 30000) recentAnnouncements.delete(k);
    }
  }
  return false;
}

// Singleton <audio> element pre-creato, "keepalive" per Chrome autoplay policy.
// Inizializzato dall'AudioUnlockOverlay al primo gesture utente; riusato per
// ogni annuncio cambiando solo `.src` e chiamando `.play()`.
declare global {
  // eslint-disable-next-line no-var
  var __kdsAnnouncementAudio: HTMLAudioElement | undefined;
}

function getOrCreateAnnouncementAudio(): HTMLAudioElement {
  if (typeof window === "undefined") return new Audio();
  if (!window.__kdsAnnouncementAudio) {
    window.__kdsAnnouncementAudio = new Audio();
    window.__kdsAnnouncementAudio.preload = "auto";
  }
  return window.__kdsAnnouncementAudio;
}

function playAudioToCompletion(url: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const audio = getOrCreateAnnouncementAudio();
      // Cleanup handler precedenti per evitare leak
      audio.onended = null;
      audio.onerror = null;
      // NO audio.crossOrigin: S3 non ritorna CORS header e il redirect 302
      // dal nostro endpoint -> S3 fallisce con "blocked by CORS policy".
      // Senza crossOrigin il browser permette playback anche cross-origin.
      audio.src = url;
      const safety = window.setTimeout(() => resolve(), 10000);
      audio.onended = () => {
        window.clearTimeout(safety);
        // eslint-disable-next-line no-console
        console.log("[KdsVoice] audio ended");
        resolve();
      };
      audio.onerror = (e) => {
        window.clearTimeout(safety);
        // eslint-disable-next-line no-console
        console.warn("[KdsVoice] audio error", e);
        resolve();
      };
      // eslint-disable-next-line no-console
      console.log("[KdsVoice] audio play start", url.slice(0, 80));
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          // eslint-disable-next-line no-console
          console.log("[KdsVoice] audio play() resolved");
        }).catch((err) => {
          window.clearTimeout(safety);
          // eslint-disable-next-line no-console
          console.warn("[KdsVoice] audio.play() rejected:", err?.message || err);
          resolve();
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[KdsVoice] audio exception", e);
      resolve();
    }
  });
}

// Web Speech API fallback RIMOSSO (feature 022): se ElevenLabs fallisce
// preferiamo silenzio alla voce sistema robotica del browser che non
// corrisponde alla personality scelta. Per ripristinare il fallback in
// emergenza vedi git history su questo file (commit eef4455c rimosso).

export function useAudio() {
  const howlsRef = useRef<Map<SoundName, Howl>>(new Map());

  const play = useCallback((sound: SoundName) => {
    let howl = howlsRef.current.get(sound);
    if (!howl) {
      howl = new Howl({ src: [SOUNDS[sound]], volume: 0.7 });
      howlsRef.current.set(sound, howl);
    }
    howl.play();
  }, []);

  /**
   * Notifica al backend un evento KDS e riproduce l'annuncio risultante
   * (audio AI o fallback Web Speech). Serializzato via promise queue
   * cosi' il secondo annuncio aspetta che il primo finisca (FR-014).
   */
  const announce = useCallback(
    (
      eventType: "new_order" | "course_fired" | "item_ready" | "nag_overdue",
      payload: Record<string, unknown>,
    ): Promise<void> => {
      // Dedup: stesso eventType + payload entro 2s → skip (backend WS emette
      // doppio per via di send locale + Redis pub/sub re-emit).
      const dedupKey = `${eventType}:${JSON.stringify(payload)}`;
      if (shouldDedup(dedupKey)) {
        // eslint-disable-next-line no-console
        console.log("[KdsVoice] dedup skip", eventType);
        return Promise.resolve();
      }
      // eslint-disable-next-line no-console
      console.log("[KdsVoice] announce →", eventType, payload);
      return enqueue(async () => {
        const r = await kdsVoiceApi.announce(eventType, payload);
        // eslint-disable-next-line no-console
        console.log("[KdsVoice] announce ←", {
          eventType,
          voiceId: (r as any)?.voiceId,
          source: (r as any)?.source,
          text: (r as any)?.generatedText?.slice(0, 100),
          cached: (r as any)?.cached,
        });
        if (!r || r.silent) return;
        if (r.audioUrl) {
          // Il backend restituisce path relativo (/api/kitchen/...) o URL assoluto.
          // Usiamo direttamente, fetch lo risolve sull'origin corrente.
          await playAudioToCompletion(r.audioUrl);
          return;
        }
        if (r.fallbackText) {
          // ElevenLabs/LLM non disponibile → la Web Speech API del browser
          // userebbe la voce di sistema (femminile robotica su Android/Chrome)
          // che NON corrisponde alla personality scelta. Meglio silenzio:
          // l'utente preferisce non sentire un annuncio piuttosto che sentire
          // Armando con voce di donna robotica.
          // eslint-disable-next-line no-console
          console.warn(
            "[KdsVoice] backend fallback (silent): " + (r as any).source,
            r.fallbackText,
          );
          return;
        }
      });
    },
    [],
  );

  return { play, announce };
}
